import React, { useContext, useEffect, useState } from 'react';
import { PdfViewer, VideoPlayer, PageLoader } from '@producer/ui-kit';

import { Asset, Comment } from '../../../app/entities';
import { AssetViewerImage } from './AssetViewerImage';
import { PagePlaceholder } from '../../../components/page-placeholder/page-placeholder';
import { download } from '../../../lib/utils/download';
import { secondsToHms } from '../../../lib/utils/Convertor';
import { LoaderContainer } from '../../../routes/project/components/step-container';
import { CommentContext } from '../../../core/contexts/comment-context/comment.context';
import { Editor } from '../../../components/editor/editor';

interface AssetPLayerProps {
  timeTagRef: React.RefObject<HTMLSpanElement>;
  asset: Asset;
  onSelectComment?: (comment: Comment) => Promise<void>;
}

export const AssetPlayer: React.FC<AssetPLayerProps> = ({ timeTagRef, asset, onSelectComment }) => {
  const [src, setSrc] = useState<null | string>(null);

  const { playerRef, comments, pdfCurrentPage, setPdfCurrentPage, selectedComment } =
    useContext(CommentContext);

  const handleDownloadFile = () => {
    download(asset._id);
  };

  useEffect(() => {
    if (asset.type === 'note') return;

    asset.getPlaybackLink().then((url) => {
      setSrc(url);
    });
  }, [asset]);

  const handleSelectComment = async (commentId?: string) => {
    if (!commentId) {
      return;
    } else {
      const comment = comments.find((el) => el._id === commentId);

      if (comment) {
        onSelectComment?.(comment);
      }
    }
  };

  const renderComponent = () => {
    if (!src) {
      return;
    }

    if (asset.fileType?.startsWith('video')) {
      const filterComments = comments
        ?.filter((el) => el.timestamp !== undefined && el.timestamp !== null)
        .map((comment) => ({
          ...comment,
          authorName: comment.authorName || '',
          avatarSrc: comment.author?.avatar || '',
        }));

      return (
        <VideoPlayer
          ref={playerRef}
          url={src}
          onTimeUpdate={(currentTime) => {
            if (!timeTagRef?.current) {
              return;
            }

            timeTagRef.current!.innerHTML = secondsToHms(currentTime);
          }}
          comments={filterComments}
          onSelectComment={handleSelectComment}
          annotations={selectedComment?.annotations || []}
          canAnnotate
        />
      );
    }

    if (asset.fileType?.startsWith('image')) {
      return <AssetViewerImage asset={asset} src={src} />;
    }

    if (asset.fileType === 'application/pdf') {
      return (
        <PdfViewer
          url={src}
          currentPage={pdfCurrentPage || 1}
          onPageChange={(page) => {
            if (setPdfCurrentPage) {
              setPdfCurrentPage(page);
            }
          }}
        />
      );
    }

    return (
      <PagePlaceholder
        description="This file type can't be displayed in the browser, you need to download the file"
        actionButton={{ text: 'Download', onClick: handleDownloadFile }}
      />
    );
  };

  if (asset.type === 'note') {
    return (
      <Editor
        content={asset.content}
        onChange={(text) => {
          asset.update({ content: text });
        }}
      />
    );
  }

  if (!src) {
    return (
      <LoaderContainer>
        <PageLoader />
      </LoaderContainer>
    );
  }

  return <>{renderComponent()}</>;
};
